import { useState, useCallback, useEffect } from 'react'
import req from '../../../../../api/request'
import { UpcomingGame } from '../../../../../types/PlayerAttendance'

interface AttendanceState {
  attendance: UpcomingGame[]
  isLoading: boolean
  error: string | null
}

interface UseAttendanceResult {
  attendanceState: AttendanceState
  readAttendanceInformation: (refresh?: boolean) => Promise<void>
  updateAttendanceStatus: (
    playerId: string,
    gameId: string,
    rosterId: string,
    status: number | null
  ) => Promise<void>
  updateBringingRefreshments: (params: {
    gameId: string
    rosterId: string
    playerId?: string
    officialId?: string
  }) => Promise<void>
}

interface UseAttendanceOptions {
  autoFetch?: boolean
}

export const useAttendance = (
  options: UseAttendanceOptions = {}
): UseAttendanceResult => {
  const { autoFetch = true } = options

  const [attendanceState, setAttendanceState] = useState<AttendanceState>({
    attendance: [],
    isLoading: false,
    error: null,
  })

  const readAttendanceInformation = useCallback(async (refresh = true) => {
    if (refresh) {
      setAttendanceState((prevState) => ({
        ...prevState,
        isLoading: true,
        error: null,
      }))
    }

    try {
      const attendanceData: UpcomingGame[] = []

      const { data: playerAttendanceData } = await req(
        '/home/player-attendance'
      )
      if (playerAttendanceData?.upcomingGames) {
        attendanceData.push(...playerAttendanceData.upcomingGames)
      }

      // Fetch team official attendance data
      const { data: teamOfficialAttendanceData } = await req(
        '/home/team-official-attendance'
      )
      if (teamOfficialAttendanceData?.upcomingGames) {
        teamOfficialAttendanceData.upcomingGames.forEach(
          (teamOfficialAttendance: UpcomingGame) => {
            const index = attendanceData.findIndex(
              (attendance) => attendance.id === teamOfficialAttendance.id
            )
            if (index !== -1) {
              const existing = attendanceData[index]
              if (!existing.home_team.videographer_user) {
                existing.home_team.videographer_user =
                  teamOfficialAttendance.home_team?.videographer_user
              }
              if (!existing.visiting_team.videographer_user) {
                existing.visiting_team.videographer_user =
                  teamOfficialAttendance.visiting_team?.videographer_user
              }
              existing.myTeamOfficials = teamOfficialAttendance.myTeamOfficials
              existing.show_waiver = teamOfficialAttendance.show_waiver
              existing.show_video = teamOfficialAttendance.show_video
            } else {
              attendanceData.push(teamOfficialAttendance)
            }
          }
        )
      }

      // Sort and filter games
      const sortedGames = attendanceData
        .sort(
          (a, b) =>
            new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
        )
        .filter(
          (game) =>
            (game.myPlayers?.length ?? 0) > 0 ||
            (game.myTeamOfficials?.length ?? 0) > 0
        )

      setAttendanceState((prevState) => ({
        ...prevState,
        attendance: sortedGames,
        isLoading: false,
        error: null,
      }))
    } catch (e) {
      setAttendanceState((prevState) => ({
        ...prevState,
        isLoading: false,
        error: 'An error occurred when trying to read attendance',
      }))
    }
  }, [])

  const updateAttendanceStatus = useCallback(
    async (
      playerId: string,
      gameId: string,
      rosterId: string,
      status: number | null
    ) => {
      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/players/${playerId}/attendance`,
          {
            method: 'PUT',
            body: JSON.stringify({ attendance_status: status }),
          }
        )
        await readAttendanceInformation(false)
      } catch (e) {
        setAttendanceState((prevState) => ({
          ...prevState,
          error: 'An error occurred when updating attendance status',
        }))
      }
    },
    [readAttendanceInformation]
  )

  const updateBringingRefreshments = useCallback(
    async ({
      gameId,
      rosterId,
      playerId,
      officialId,
    }: {
      gameId: string
      rosterId: string
      playerId?: string
      officialId?: string
    }) => {
      const segment = officialId
        ? `team-officials/${officialId}`
        : `players/${playerId}`
      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/${segment}/attendance`,
          {
            method: 'PUT',
            body: JSON.stringify({ bringing_refreshments: true }),
          }
        )
        await readAttendanceInformation(false)
      } catch (e) {
        setAttendanceState((prevState) => ({
          ...prevState,
          error: 'An error occurred when updating refreshments',
        }))
      }
    },
    [readAttendanceInformation]
  )

  useEffect(() => {
    if (autoFetch) {
      readAttendanceInformation()
    }
  }, [autoFetch, readAttendanceInformation])

  return {
    attendanceState,
    readAttendanceInformation,
    updateAttendanceStatus,
    updateBringingRefreshments,
  }
}
